import React, { useState, useEffect, useCallback } from 'react'
import { Box, Text, Grid, AspectRatio, Group, Modal, Button, Stack } from '@mantine/core'
import { VehicleService } from '@/api-proxy/serviceAvailability'
import { ServiceUnreachableBanner } from './VehicleServiceNotice'
import { Rectangle } from './types'
import { FaSync } from 'react-icons/fa'
import IconButton from '../Common/IconButton'
import { isEmpty } from 'lodash-es'
import { useTranslation } from 'react-i18next'

const imageStyles = {
  width: '100%',
  height: '100%',
  border: '1px solid #ccc',
  borderRadius: '8px',
  objectFit: 'cover' as const,
}

const loadingBoxStyles = {
  border: '1px dashed #ccc',
  position: 'relative' as const,
  height: '100%',
  borderRadius: '8px',
}

const CropImage: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <img src={src} alt={alt} style={imageStyles} />
)

const LoadingBox: React.FC<{ children: React.ReactNode; textSize?: string; height?: string }> = ({
  children,
  textSize = 'md',
  height = undefined,
}) => (
  <Box style={{ ...loadingBoxStyles, height }}>
    <Text
      size={textSize}
      c="dimmed"
      ta="center"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%',
      }}
    >
      {children}
    </Text>
  </Box>
)

const ComparisonColumn: React.FC<{
  title: string
  crop: string | null
  alt: string
  loadingText: string
  isLoading: boolean
  onRefetch: () => void
  withConfirmation?: boolean
}> = ({ title, crop, alt, loadingText, isLoading, onRefetch, withConfirmation = true }) => {
  const { t } = useTranslation(['hardware', 'common'])
  const [modalOpened, setModalOpened] = useState(false)

  const handleRefetch = () => {
    onRefetch()
    setModalOpened(false)
  }

  return (
    <Grid.Col span={6}>
      <Group justify="space-between" align="center" mb="sm" styles={{ root: { alignItems: 'center' } }}>
        <Text size="md">{title}</Text>
        <IconButton
          label={t('refetchImage')}
          onClick={() => (withConfirmation ? setModalOpened(true) : onRefetch())}
          icon={<FaSync />}
          disabled={isLoading}
        />
        {withConfirmation && (
          <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title={t('confirmRefetch')} centered>
            <Stack gap="md">
              <Text size="sm">{t('confirmRefetchMessage')}</Text>
              <Group gap="xs" justify="flex-end">
                <Button variant="outline" onClick={() => setModalOpened(false)}>
                  {t('cancel', { ns: 'common' })}
                </Button>
                <Button onClick={handleRefetch} loading={isLoading}>
                  {t('refetch')}
                </Button>
              </Group>
            </Stack>
          </Modal>
        )}
      </Group>
      <AspectRatio ratio={16 / 9}>
        {isLoading ? (
          <LoadingBox textSize="md">{loadingText}</LoadingBox>
        ) : crop ? (
          <CropImage src={crop} alt={alt} />
        ) : (
          <LoadingBox textSize="md">{loadingText}</LoadingBox>
        )}
      </AspectRatio>
    </Grid.Col>
  )
}

const FocusComparisonCards: React.FC<{
  originalImageUrl?: string
  adjustedImageUrl?: string
  selectedRectangle?: Rectangle | null
  isOriginalLoading: boolean
  isAdjustedLoading: boolean
  onRefetchOriginal: () => void
  onRefetchAdjusted: () => void
  unreachableServices?: VehicleService[]
  onRetry?: () => void
}> = ({
  originalImageUrl,
  adjustedImageUrl,
  selectedRectangle,
  isOriginalLoading,
  isAdjustedLoading,
  onRefetchOriginal,
  onRefetchAdjusted,
  unreachableServices = [],
  onRetry = () => undefined,
}) => {
  const { t } = useTranslation(['hardware', 'common'])
  const [originalCrop, setOriginalCrop] = useState<string | null>(null)
  const [adjustedCrop, setAdjustedCrop] = useState<string | null>(null)

  const extractCropFromImage = useCallback(
    (imageUrl: string, rectangle: Rectangle, setCrop: (crop: string) => void) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) return

        canvas.width = rectangle.width
        canvas.height = rectangle.height

        ctx.drawImage(
          img,
          rectangle.x,
          rectangle.y,
          rectangle.width,
          rectangle.height,
          0,
          0,
          rectangle.width,
          rectangle.height
        )

        const cropDataUrl = canvas.toDataURL('image/png')
        setCrop(cropDataUrl)
      }
      img.src = imageUrl
    },
    []
  )

  // Extract crop when square changes
  useEffect(() => {
    if (selectedRectangle && originalImageUrl) {
      extractCropFromImage(originalImageUrl, selectedRectangle, setOriginalCrop)
    } else {
      setOriginalCrop(null)
    }
  }, [selectedRectangle, originalImageUrl, extractCropFromImage])

  // Extract crop from adjusted image when it's available
  useEffect(() => {
    if (selectedRectangle && adjustedImageUrl) {
      extractCropFromImage(adjustedImageUrl, selectedRectangle, setAdjustedCrop)
    } else {
      setAdjustedCrop(null)
    }
  }, [selectedRectangle, adjustedImageUrl, extractCropFromImage])

  return (
    <Box style={{ width: '100%' }}>
      <Text mb="sm">{t('focusComparison')}</Text>
      {!isEmpty(unreachableServices) ? (
        <ServiceUnreachableBanner services={unreachableServices} onRetry={onRetry} />
      ) : selectedRectangle ? (
        <Grid>
          <ComparisonColumn
            title={t('before')}
            crop={originalCrop}
            alt="Original crop"
            loadingText={t('loadingCrop')}
            isLoading={isOriginalLoading}
            onRefetch={onRefetchOriginal}
          />
          <ComparisonColumn
            isLoading={isAdjustedLoading}
            title={t('after')}
            crop={adjustedCrop}
            alt="Adjusted crop"
            loadingText={t('updateFocusToSeeResult')}
            onRefetch={onRefetchAdjusted}
            withConfirmation={false}
          />
        </Grid>
      ) : (
        <AspectRatio ratio={16 / 9}>
          <LoadingBox textSize="md" height="250px">
            {t('drawRectangle')}
          </LoadingBox>
        </AspectRatio>
      )}
    </Box>
  )
}

export default FocusComparisonCards
